import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DescriptionIcon from '@mui/icons-material/Description';
import ImageIcon from '@mui/icons-material/Image';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import SendIcon from '@mui/icons-material/Send';
import VideocamIcon from '@mui/icons-material/Videocam';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import { alpha } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import useCurrentUser from 'hooks/useCurrentUser';
import { type FC, useMemo, useState } from 'react';
import { useGetBusinessUnitTemplatesQuery } from 'services/api/businessUnits.api';
import { usePostTemplateMessageMutation } from 'services/api/messages';
import type { Client } from 'services/types/client';
import type { SendTemplate, TemplateResponse } from 'services/types/message';
import {
  GLASS_BACKGROUND,
  GLASS_BLUR,
  NAVY,
  RADIUS_CARD,
  RADIUS_COIN,
  RADIUS_ITEM,
  SHADOW,
  SHADOW_CARD,
  STATUS_GOOD,
  STATUS_WARN,
  SURFACE_CONTAINER_LOWEST,
  YELLOW,
} from 'styles/tokens';
import capitalize from 'utils/capitalize';

// ─── Draft builder ─────────────────────────────────────────────────────────────

type Template = TemplateResponse[number];
type Components = NonNullable<SendTemplate['components']>;

interface TemplateDraft {
  template: Template;
  /** Ready-to-send components payload, or undefined when the template takes no parameters. */
  components: SendTemplate['components'];
  headerText: string | null;
  headerMediaFormat: 'IMAGE' | 'DOCUMENT' | 'VIDEO' | null;
  body: string;
  footer: string | null;
  buttons: { text: string }[];
}

/**
 * Resolves a WhatsApp named parameter to a value taken from the client / seller.
 * Mirrors the auto-fill map used in TemplateMessageModal so both surfaces fill
 * the exact same magic names (cliente, ejecutivo, marca, auto, sucursal…).
 * Returns '' when the field is unknown or empty.
 */
const resolveNamedParam = (paramName: string, client: Client, userName?: string | null): string => {
  const p = paramName.toLowerCase();
  if (['user', 'usuario', 'ejecutivo', 'vendedor', 'nombre_vendedor'].includes(p)) return capitalize(userName);
  if (['client', 'cliente', 'nombre_cliente', 'nombre', 'name'].includes(p)) return capitalize(client.firstName);
  if (['brand', 'marca'].includes(p)) return capitalize(client.brandOfInterest);
  if (['model', 'modelo', 'auto', 'car'].includes(p)) return capitalize(client.modelsOfInterest?.[0]);
  if (['branch_office', 'sucursal'].includes(p)) return capitalize(client.branchOffice);
  return '';
};

/**
 * Builds a ready-to-send draft from a template — but only when EVERY parameter
 * it needs can be auto-filled from the client / seller data. Returns null when
 * the template still requires the full modal, i.e. it has:
 *   - positional placeholders ({{1}}) we can't name,
 *   - named params whose source field is empty or unknown,
 *   - parameterised button URLs.
 * Media-header templates (image/doc/video) are allowed: the backend resolves the
 * sample media from the template itself, matching the existing modal behaviour.
 */
const buildTemplateDraft = (template: Template, client: Client, userName?: string | null): TemplateDraft | null => {
  const components: Components = [];
  let headerText: string | null = null;
  let headerMediaFormat: TemplateDraft['headerMediaFormat'] = null;
  let body = '';
  let footer: string | null = null;
  const buttons: { text: string }[] = [];

  for (const component of template.components) {
    if (component.type === 'BUTTONS') {
      for (const button of component.buttons ?? []) {
        // A button URL with a {{n}} placeholder needs a value we can't infer.
        if (button.url && /{{[1-9]}}/.test(button.url)) return null;
        buttons.push({ text: button.text });
      }
    } else {
      const text = component.text ?? '';
      // Positional placeholders ({{1}}) carry no name → can't be auto-filled.
      if (/{{[1-9]}}/.test(text)) return null;

      const resolved: Components[number]['parameters'] = [];
      let filledText = text;
      for (const { param_name } of component.example?.body_text_named_params ?? []) {
        const value = resolveNamedParam(param_name, client, userName);
        if (!value) return null; // a needed field is empty → fall back to the modal
        resolved.push({ type: 'text', text: value, parameter_name: param_name });
        filledText = filledText.replaceAll(`{{${param_name}}}`, value);
      }

      if (resolved.length > 0) {
        components.push({ type: component.type.toLowerCase() as 'header' | 'body' | 'footer', parameters: resolved });
      }

      if (component.type === 'HEADER') {
        if (component.format && component.format !== 'TEXT') headerMediaFormat = component.format;
        else headerText = filledText;
      } else if (component.type === 'BODY') {
        body = filledText;
      } else if (component.type === 'FOOTER') {
        footer = filledText;
      }
    }
  }

  // Any leftover {{...}} means a placeholder we couldn't resolve → not ready.
  if ([headerText, body, footer].some((t) => t && /{{.+?}}/.test(t))) return null;

  return {
    template,
    components: components.length > 0 ? components : undefined,
    headerText,
    headerMediaFormat,
    body,
    footer,
    buttons,
  };
};

// ─── Component ──────────────────────────────────────────────────────────────────

/** How many ready-to-send templates to surface inline before falling back to "ver todas". */
const MAX_QUICK = 5;

interface Props {
  client: Client;
  isEmptyConversation: boolean;
  onOpenFullModal: () => void;
  onSent: () => void;
}

const MediaChip: FC<{ format: NonNullable<TemplateDraft['headerMediaFormat']> }> = ({ format }) => {
  const config = {
    IMAGE: { icon: <ImageIcon sx={{ fontSize: 14 }} />, label: 'Imagen' },
    DOCUMENT: { icon: <DescriptionIcon sx={{ fontSize: 14 }} />, label: 'Documento' },
    VIDEO: { icon: <VideocamIcon sx={{ fontSize: 14 }} />, label: 'Video' },
  }[format];

  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        px: 1,
        py: 0.25,
        mb: 0.75,
        borderRadius: RADIUS_ITEM,
        backgroundColor: alpha(NAVY, 0.06),
        color: alpha(NAVY, 0.7),
        fontSize: '0.7rem',
        fontWeight: 600,
      }}
    >
      {config.icon}
      {config.label}
    </Box>
  );
};

const TemplateCard: FC<{ draft: TemplateDraft; sending: boolean; disabled: boolean; onSend: () => void }> = ({
  draft,
  sending,
  disabled,
  onSend,
}) => (
  <Box
    sx={{
      backgroundColor: SURFACE_CONTAINER_LOWEST,
      borderRadius: RADIUS_CARD,
      boxShadow: SHADOW_CARD,
      p: 2,
      display: 'flex',
      flexDirection: { xs: 'column', sm: 'row' },
      alignItems: { xs: 'stretch', sm: 'center' },
      gap: 1.5,
    }}
  >
    <Box sx={{ flex: 1, minWidth: 0 }}>
      {draft.headerMediaFormat && <MediaChip format={draft.headerMediaFormat} />}
      {draft.headerText && (
        <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: NAVY, mb: 0.25 }}>{draft.headerText}</Typography>
      )}
      <Typography sx={{ fontSize: '0.9rem', color: alpha(NAVY, 0.85), whiteSpace: 'pre-wrap', lineHeight: 1.45 }}>
        {draft.body}
      </Typography>
      {draft.footer && (
        <Typography sx={{ fontSize: '0.75rem', color: alpha(NAVY, 0.5), mt: 0.5 }}>{draft.footer}</Typography>
      )}
      {draft.buttons.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1 }}>
          {draft.buttons.map((button) => (
            <Box
              key={button.text}
              sx={{
                px: 1.25,
                py: 0.25,
                borderRadius: RADIUS_ITEM,
                backgroundColor: alpha(NAVY, 0.05),
                color: alpha(NAVY, 0.6),
                fontSize: '0.72rem',
                fontWeight: 600,
              }}
            >
              {button.text}
            </Box>
          ))}
        </Box>
      )}
      <Typography
        sx={{
          mt: 1,
          fontSize: '0.62rem',
          fontWeight: 500,
          color: alpha(NAVY, 0.3),
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {draft.template.name} · {draft.template.category.toLowerCase()}
      </Typography>
    </Box>

    <Button
      onClick={onSend}
      disabled={disabled || sending}
      sx={{
        alignSelf: { xs: 'flex-end', sm: 'center' },
        flexShrink: 0,
        backgroundColor: YELLOW,
        color: NAVY,
        borderRadius: RADIUS_COIN,
        fontWeight: 600,
        textTransform: 'none',
        px: 2.5,
        py: 0.75,
        minWidth: 96,
        boxShadow: 'none',
        '&:hover': { backgroundColor: YELLOW, opacity: 0.9, boxShadow: SHADOW },
        '&.Mui-disabled': { backgroundColor: YELLOW, color: NAVY, opacity: 0.4 },
      }}
    >
      {sending ? (
        <CircularProgress size={18} sx={{ color: NAVY }} />
      ) : (
        <>
          Enviar
          <SendIcon sx={{ fontSize: 16, ml: 0.75 }} />
        </>
      )}
    </Button>
  </Box>
);

/**
 * Inline replacement for the closed-conversation footer in the chat (general
 * client view + seller iframes: Autoland, Bruno Fritsch, Pompeyo, AutosRelif).
 * Surfaces the 5 most recently edited approved templates that can be fully
 * auto-filled from the client data, each one ready to send in a single tap — no
 * modal, no searching. Surfaced to sellers as "mensajes rápidos" (no WhatsApp
 * "plantilla" jargon). Anything that needs manual parameters or template
 * creation falls back to the full TemplateMessageModal via "Ver más opciones de mensajes".
 */
const QuickTemplates: FC<Props> = ({ client, isEmptyConversation, onOpenFullModal, onSent }) => {
  const { data: templates, isLoading, error } = useGetBusinessUnitTemplatesQuery(client.businessUnitId);
  const currentUser = useCurrentUser();
  const [postTemplate] = usePostTemplateMessageMutation();
  const [sendingName, setSendingName] = useState<string | null>(null);
  const [sentName, setSentName] = useState<string | null>(null);
  const [sendError, setSendError] = useState<string | null>(null);
  // Open by default; lets the seller collapse the quick templates to see more chat.
  const [collapsed, setCollapsed] = useState(true);

  // The request failed (with no cached data) or the client simply has no templates:
  // there's nothing to quick-send, so fall back to the plain "open the modal" button.
  const loadFailed = Boolean(error) && !templates;
  const hasNoTemplates = Boolean(templates) && templates?.length === 0;

  const drafts = useMemo(() => {
    if (!templates) return [];
    const ranked = [...templates]
      .filter((t) => ['approved', 'reinstated'].includes(t.status?.toLowerCase()))
      // Most recently edited first.
      .sort((a, b) => new Date(b.modified_at).getTime() - new Date(a.modified_at).getTime());

    const result: TemplateDraft[] = [];
    for (const template of ranked) {
      const draft = buildTemplateDraft(template, client, currentUser?.name);
      if (draft) result.push(draft);
      if (result.length >= MAX_QUICK) break;
    }
    return result;
  }, [templates, client, currentUser?.name]);

  const handleSend = (draft: TemplateDraft) => {
    setSendingName(draft.template.name);
    setSendError(null);
    postTemplate({
      clientId: client.id,
      templateName: draft.template.name,
      components: draft.components,
      // The client already drives the conversation (AI off by default in the
      // iframe); leave the AI status untouched.
      aiStatus: null,
    })
      // .unwrap() makes the promise reject on a backend error — without it RTK
      // Query resolves with { error } and we'd wrongly show "sent".
      .unwrap()
      .then(() => {
        setSentName(draft.template.name);
        onSent();
      })
      .catch((err) => {
        console.error('[QuickTemplates] Error sending template', err);
        // Surface the backend reason (e.g. "No se pueden enviar templates a
        // clientes de Demo Chat.") instead of a generic message.
        const backendMsg = typeof err?.data === 'string' ? err.data : err?.data?.message;
        setSendError(backendMsg || 'No se pudo enviar el mensaje. Inténtalo de nuevo.');
      })
      .finally(() => setSendingName(null));
  };

  // Plain descriptive hint (not a chip/button): a friendly invitation to start
  // or resume the conversation with a quick message.
  const statusHint = (
    <Box
      onClick={() => setCollapsed((c) => !c)}
      role="button"
      aria-expanded={!collapsed}
      aria-label={collapsed ? 'Mostrar plantillas' : 'Ocultar plantillas'}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 0.5,
        cursor: 'pointer',
        userSelect: 'none',
      }}
    >
      <Typography sx={{ fontSize: '0.8rem', fontWeight: 500, color: alpha(NAVY, 0.55) }}>
        {isEmptyConversation ? 'Inicia con un mensaje rápido' : 'Reanuda con un mensaje rápido'}
      </Typography>
      {collapsed ? (
        <KeyboardArrowDownIcon fontSize="small" sx={{ color: alpha(NAVY, 0.55) }} />
      ) : (
        <KeyboardArrowUpIcon fontSize="small" sx={{ color: alpha(NAVY, 0.55) }} />
      )}
    </Box>
  );

  // Escape hatch to the full library (search / manual params / creation). Always
  // visible — also alongside the quick cards — as a muted secondary so it never
  // competes with the cards' yellow "Enviar".
  const seeAllButton = (label: string) => (
    <Button
      onClick={onOpenFullModal}
      sx={{
        color: NAVY,
        backgroundColor: alpha(NAVY, 0.06),
        borderRadius: RADIUS_COIN,
        fontWeight: 600,
        textTransform: 'none',
        px: 2.5,
        py: 0.75,
        boxShadow: 'none',
        '&:hover': { backgroundColor: alpha(NAVY, 0.1) },
      }}
    >
      {label}
    </Button>
  );

  // The original closed-conversation CTA, used when there are no quick templates to offer.
  const fallbackButton = (
    <Button
      onClick={onOpenFullModal}
      sx={{
        backgroundColor: YELLOW,
        color: NAVY,
        borderRadius: RADIUS_COIN,
        fontWeight: 600,
        textTransform: 'none',
        px: 2.5,
        py: 0.75,
        boxShadow: 'none',
        '&:hover': { backgroundColor: YELLOW, opacity: 0.9, boxShadow: SHADOW },
        '&.Mui-disabled': { backgroundColor: YELLOW, color: NAVY, opacity: 0.4 },
      }}
    >
      {isEmptyConversation ? 'Iniciar conversación' : 'Retomar conversación'}
    </Button>
  );

  return (
    <Box
      sx={{
        mx: 2,
        mb: 2,
        mt: 1,
        px: { xs: 2, sm: 3 },
        py: 2.5,
        backgroundColor: GLASS_BACKGROUND,
        backdropFilter: GLASS_BLUR,
        borderRadius: RADIUS_CARD,
        boxShadow: SHADOW,
        maxHeight: '50vh',
        overflowY: 'auto',
      }}
    >
      {sentName ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: STATUS_GOOD }}>
          <CheckCircleIcon sx={{ fontSize: 20 }} />
          <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, color: NAVY }}>
            Mensaje enviado — espera la respuesta del cliente para seguir conversando.
          </Typography>
        </Box>
      ) : (
        statusHint
      )}

      {!sentName && !collapsed && sendError && (
        <Typography sx={{ mt: 1, fontSize: '0.8rem', fontWeight: 600, color: STATUS_WARN }}>{sendError}</Typography>
      )}

      {!sentName && !collapsed && (
        <Box sx={{ mt: 2 }}>
          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={24} sx={{ color: NAVY }} />
            </Box>
          ) : loadFailed || hasNoTemplates ? (
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>{fallbackButton}</Box>
          ) : drafts.length > 0 ? (
            <>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                {drafts.map((draft) => (
                  <TemplateCard
                    key={draft.template.name}
                    draft={draft}
                    sending={sendingName === draft.template.name}
                    disabled={sendingName !== null && sendingName !== draft.template.name}
                    onSend={() => handleSend(draft)}
                  />
                ))}
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                {seeAllButton('Ver más opciones de mensajes')}
              </Box>
            </>
          ) : (
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              {seeAllButton('Ver opciones de mensajes para enviar')}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default QuickTemplates;
